import { useCallback, useEffect, useMemo, useState } from "react";
import { API } from "../../../services/api";
import { emptyMaintenanceProcedure, emptyParkItem } from "../model/materielConstants";
import { filterParkItems, parkItemPayload, parkItemToForm } from "../model/parkRules";

const CONFIG = { noRedirectOn401: true };
const isCompactWidth = () => (globalThis.innerWidth || 1600) < 1420;

// Parc matériel d'un service : matériels et procédures chargés ensemble, filtres, matériel sélectionné et
// panneau latéral affiché (fiche, formulaire ou procédures).
export function useEquipmentPark(serviceKey) {
    const endpoint = `/medical-equipment/services/${serviceKey}/park`;
    const proceduresEndpoint = `${endpoint}/maintenance-procedures`;
    const [items, setItems] = useState([]);
    const [procedures, setProcedures] = useState([]);
    const [selectedId, setSelectedId] = useState(null);
    const [query, setQuery] = useState("");
    const [statusFilter, setStatusFilter] = useState("");
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState(null);
    const [panelMode, setPanelMode] = useState("detail");
    const [isParkCompact, setIsParkCompact] = useState(isCompactWidth);

    const refresh = useCallback(() => {
        setLoading(true);
        setError(null);
        Promise.all([
            API.get(endpoint, CONFIG),
            API.get(proceduresEndpoint, CONFIG),
        ])
            .then(([itemsResponse, proceduresResponse]) => {
                setItems(itemsResponse.data || []);
                setProcedures(proceduresResponse.data || []);
                setSelectedId(current => current || itemsResponse.data?.[0]?.id || null);
            })
            .catch(() => setError("Impossible de charger le parc matériel."))
            .finally(() => setLoading(false));
    }, [endpoint, proceduresEndpoint]);

    useEffect(() => { refresh(); }, [refresh]);
    useEffect(() => {
        const onResize = () => setIsParkCompact(isCompactWidth());
        globalThis.addEventListener?.("resize", onResize);
        return () => globalThis.removeEventListener?.("resize", onResize);
    }, []);

    const filteredItems = useMemo(() => filterParkItems(items, query, statusFilter), [items, query, statusFilter]);
    const selectedItem = filteredItems.find(item => item.id === selectedId) || filteredItems[0] || null;

    useEffect(() => {
        if (selectedItem && selectedItem.id !== selectedId) {
            setSelectedId(selectedItem.id);
        }
    }, [selectedId, selectedItem]);

    const replaceItem = (id, item) => setItems(prev => prev.map(current => current.id === id ? item : current));

    return {
        endpoint, proceduresEndpoint, items, setItems, replaceItem, procedures, setProcedures, selectedId, setSelectedId,
        query, setQuery, statusFilter, setStatusFilter, filteredItems, selectedItem, loading, saving, setSaving,
        error, setError, panelMode, setPanelMode, isParkCompact, refresh,
    };
}

// Formulaire d'ajout ou de modification d'un matériel, envoyé en multipart avec le contrat et la procédure
// d'installation éventuels ; suppression d'un matériel après confirmation.
export function useParkItemForm(park) {
    const { endpoint, setItems, selectedId, setSelectedId, setSaving, setError, setPanelMode } = park;
    const [form, setForm] = useState(emptyParkItem);
    const [editingItem, setEditingItem] = useState(null);
    const [rentalContract, setRentalContract] = useState(null);
    const [installationProcedure, setInstallationProcedure] = useState(null);

    const openForm = (nextForm, item, mode) => {
        setForm(nextForm);
        setEditingItem(item);
        setRentalContract(null);
        setInstallationProcedure(null);
        setPanelMode(mode);
    };
    const resetForm = () => openForm(emptyParkItem, null, "detail");
    const startAdd = () => openForm(emptyParkItem, null, "form");
    const edit = item => openForm(parkItemToForm(item), item, "form");

    const makeItemFormData = () => {
        const data = new FormData();
        data.append("item", new Blob([JSON.stringify(parkItemPayload(form, editingItem))], { type: "application/json" }));
        if (rentalContract) data.append("rentalContract", rentalContract);
        if (installationProcedure) data.append("installationProcedure", installationProcedure);
        return data;
    };

    const submit = async event => {
        event.preventDefault();
        setSaving(true);
        setError(null);
        try {
            const response = editingItem
                ? await API.patch(`${endpoint}/${editingItem.id}`, makeItemFormData(), CONFIG)
                : await API.post(endpoint, makeItemFormData(), CONFIG);
            setItems(prev => editingItem
                ? prev.map(item => item.id === editingItem.id ? response.data : item)
                : [response.data, ...prev]);
            setSelectedId(response.data.id);
            resetForm();
        } catch (err) {
            setError(err.response?.data?.message || "Impossible d'enregistrer ce matériel.");
        } finally {
            setSaving(false);
        }
    };

    const remove = async item => {
        if (!globalThis.confirm(`Supprimer le matériel ${item.internalNumber} ?`)) return;
        setError(null);
        try {
            await API.delete(`${endpoint}/${item.id}`, CONFIG);
            setItems(prev => prev.filter(current => current.id !== item.id));
            if (selectedId === item.id) setSelectedId(null);
            if (editingItem?.id === item.id) resetForm();
        } catch (err) {
            setError(err.response?.data?.message || "Impossible de supprimer ce matériel.");
        }
    };

    return { form, setForm, editingItem, setRentalContract, setInstallationProcedure, resetForm, startAdd, edit, submit, remove };
}

// Procédures d'entretien : une procédure ajoutée est aussitôt sélectionnée dans le formulaire du matériel.
export function useMaintenanceProcedures(park, setItemForm) {
    const { proceduresEndpoint, setProcedures, setSaving, setError } = park;
    const [procedureForm, setProcedureForm] = useState(emptyMaintenanceProcedure);
    const [maintenanceProcedureFile, setMaintenanceProcedureFile] = useState(null);

    const createMaintenanceProcedure = async event => {
        event.preventDefault();
        setSaving(true);
        setError(null);
        const data = new FormData();
        data.append("procedure", new Blob([JSON.stringify(procedureForm)], { type: "application/json" }));
        if (maintenanceProcedureFile) data.append("file", maintenanceProcedureFile);
        try {
            const response = await API.post(proceduresEndpoint, data, CONFIG);
            setProcedures(prev => [...prev, response.data].sort((a, b) => a.name.localeCompare(b.name, "fr")));
            setItemForm(prev => ({ ...prev, maintenanceRequired: true, maintenanceProcedureId: response.data.id }));
            setProcedureForm(emptyMaintenanceProcedure);
            setMaintenanceProcedureFile(null);
        } catch (err) {
            setError(err.response?.data?.message || "Impossible d'ajouter la procédure d'entretien.");
        } finally {
            setSaving(false);
        }
    };

    return { procedureForm, setProcedureForm, setMaintenanceProcedureFile, createMaintenanceProcedure };
}
